import { useEffect, useLayoutEffect, useState } from "react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ChevronLeft, ChevronRight, X, Sparkles } from "lucide-react";
import { useOnboarding } from "@/lib/onboarding";
import { TOUR_TRACKS } from "@/lib/tourTracks";

const PADDING = 8;
const TOOLTIP_W = 360;
const TOOLTIP_H_ESTIMATE = 200;

export function ProductTour() {
  const { tourActive, stopTour, tourTrack } = useOnboarding();
  const [, setLocation] = useLocation();
  const [stepIndex, setStepIndex] = useState(0);
  const [rect, setRect] = useState<DOMRect | null>(null);

  const track = TOUR_TRACKS[tourTrack] ?? TOUR_TRACKS.quick;
  const STEPS = track.steps;
  const step = STEPS[stepIndex];

  useEffect(() => {
    if (tourActive) setStepIndex(0);
  }, [tourActive, tourTrack]);

  useEffect(() => {
    if (
      tourActive &&
      step?.route &&
      window.location.pathname.replace(/\/$/, "") !== step.route
    ) {
      setLocation(step.route);
    }
  }, [tourActive, step, setLocation]);

  useLayoutEffect(() => {
    if (!tourActive || !step) {
      setRect(null);
      return;
    }
    let cancelled = false;
    let scrollTarget: HTMLElement | null = null;

    const measureRect = () => {
      if (cancelled) return;
      const el = step.selector === "body"
        ? null
        : (document.querySelector(step.selector) as HTMLElement | null);
      if (el) setRect(el.getBoundingClientRect());
      else setRect(null);
    };

    const findAndScroll = () => {
      if (step.selector === "body") {
        setRect(null);
        return;
      }
      const el = document.querySelector(step.selector) as HTMLElement | null;
      if (el) {
        scrollTarget = el;
        el.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
        measureRect();
      } else {
        setRect(null);
      }
    };

    findAndScroll();

    const settleTimers = [60, 180, 360, 600, 1200].map((ms) =>
      window.setTimeout(measureRect, ms),
    );

    let ro: ResizeObserver | undefined;
    if (typeof ResizeObserver !== "undefined" && scrollTarget) {
      ro = new ResizeObserver(measureRect);
      ro.observe(scrollTarget);
      ro.observe(document.body);
    }

    window.addEventListener("resize", measureRect);
    window.addEventListener("scroll", measureRect, true);

    return () => {
      cancelled = true;
      settleTimers.forEach((t) => window.clearTimeout(t));
      ro?.disconnect();
      window.removeEventListener("resize", measureRect);
      window.removeEventListener("scroll", measureRect, true);
    };
  }, [tourActive, step, stepIndex]);

  useEffect(() => {
    if (!tourActive) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") stopTour();
      else if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") prev();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  if (!tourActive || !step) return null;

  function next() {
    if (stepIndex >= STEPS.length - 1) finish();
    else setStepIndex((i) => i + 1);
  }
  function prev() { setStepIndex((i) => Math.max(0, i - 1)); }
  function finish() { setStepIndex(0); stopTour(); }

  const tooltipPos = computeTooltipPosition(rect, step.placement ?? "bottom");

  return (
    <div className="fixed inset-0 z-[100] pointer-events-none" data-testid="product-tour" data-track={track.id}>
      <div className="absolute inset-0 pointer-events-auto" onClick={() => { /* swallow */ }}>
        {rect ? (
          <svg className="absolute inset-0 w-full h-full">
            <defs>
              <mask id="tour-mask">
                <rect width="100%" height="100%" fill="white" />
                <rect
                  x={Math.max(0, rect.left - PADDING)}
                  y={Math.max(0, rect.top - PADDING)}
                  width={rect.width + PADDING * 2}
                  height={rect.height + PADDING * 2}
                  rx="10" ry="10" fill="black"
                />
              </mask>
            </defs>
            <rect width="100%" height="100%" fill="rgba(15, 23, 42, 0.55)" mask="url(#tour-mask)" />
            <rect
              x={Math.max(0, rect.left - PADDING)}
              y={Math.max(0, rect.top - PADDING)}
              width={rect.width + PADDING * 2}
              height={rect.height + PADDING * 2}
              rx="10" ry="10" fill="none"
              stroke="rgba(30, 58, 95, 0.9)" strokeWidth="2"
            />
          </svg>
        ) : (
          <div className="absolute inset-0 bg-slate-900/55" />
        )}
      </div>

      <Card
        className="absolute pointer-events-auto shadow-2xl border-[#0B1834]/20"
        style={{ left: tooltipPos.left, top: tooltipPos.top, width: TOOLTIP_W }}
        data-testid="tour-tooltip"
      >
        <div className="p-5">
          <div className="flex items-start justify-between gap-2 mb-2">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-[#0B1834] flex items-center justify-center">
                <Sparkles className="h-3.5 w-3.5 text-white" />
              </div>
              <div className="flex flex-col">
                <span className="text-[11px] font-semibold text-[#0B1834] uppercase tracking-wide leading-tight">
                  {track.name} · {track.persona}
                </span>
                <span className="text-[10px] text-muted-foreground uppercase tracking-wide">
                  Step {stepIndex + 1} of {STEPS.length}
                </span>
              </div>
            </div>
            <button
              onClick={finish}
              className="text-muted-foreground hover:text-foreground rounded-md p-1 hover:bg-muted transition-colors"
              aria-label="Skip tour"
              data-testid="button-tour-skip"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <h3 className="font-semibold text-gray-900 text-base mb-1">{step.title}</h3>
          <p className="text-sm text-muted-foreground leading-relaxed">{step.body}</p>

          <div className="flex items-center justify-between mt-5">
            <Button variant="ghost" size="sm" onClick={finish} data-testid="button-tour-finish">
              {stepIndex === STEPS.length - 1 ? "Finish" : "Skip tour"}
            </Button>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={prev} disabled={stepIndex === 0} data-testid="button-tour-prev">
                <ChevronLeft className="h-4 w-4" />
                Back
              </Button>
              <Button
                size="sm"
                onClick={next}
                className="bg-[#0B1834] hover:bg-[#0A152E] text-white"
                data-testid="button-tour-next"
              >
                {stepIndex === STEPS.length - 1 ? "Done" : "Next"}
                {stepIndex < STEPS.length - 1 && <ChevronRight className="h-4 w-4 ml-1" />}
              </Button>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}

function computeTooltipPosition(rect: DOMRect | null, placement: "bottom" | "top" | "right" | "left") {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  if (!rect) {
    return { left: Math.max(16, vw / 2 - TOOLTIP_W / 2), top: Math.max(80, vh / 2 - TOOLTIP_H_ESTIMATE / 2) };
  }
  const margin = 16;
  let left = rect.left;
  let top = rect.bottom + margin;

  switch (placement) {
    case "top":
      top = rect.top - TOOLTIP_H_ESTIMATE - margin;
      left = rect.left + rect.width / 2 - TOOLTIP_W / 2;
      break;
    case "right":
      left = rect.right + margin;
      top = rect.top;
      break;
    case "left":
      left = rect.left - TOOLTIP_W - margin;
      top = rect.top;
      break;
    case "bottom":
    default:
      top = rect.bottom + margin;
      left = rect.left + rect.width / 2 - TOOLTIP_W / 2;
      break;
  }

  left = Math.max(16, Math.min(vw - TOOLTIP_W - 16, left));
  top = Math.max(16, Math.min(vh - TOOLTIP_H_ESTIMATE - 16, top));
  return { left, top };
}
